import React, {FC, useEffect, useMemo, useState} from 'react';
import {Tooltip} from 'components';
import {detachedSearchBarStyles} from 'components/Basic/Form/Select/styles';
import {FormSelect} from 'components/Basic/Form/V2';
import {format} from 'date-fns';
import dayjs from 'dayjs';
import {useBooking} from 'hooks';
import {AppointmentDurations, OptionType, SliceStatus} from 'interfaces';
import {useTranslation} from 'react-i18next';

type Props = {
  label?: string;
  loadingStatus: SliceStatus;
  selectedDate?: dayjs.Dayjs;
  isProvider: boolean;
  duration?: AppointmentDurations;
} & Partial<ReturnType<typeof useBooking>>;

const now = new Date();

const twentyFourHours = new Date().getTime() + 24 * 60 * 60 * 1000;

export const TimeSelect: FC<Props> = ({
  time,
  control,
  watch,
  loadingStatus,
  isProvider,
  duration,
  ...props
}: Props) => {
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const {t} = useTranslation();
  // Get current time and log it
  const currentTime = new Date();

  // Disable time slots earlier than current time
  const processedTimeSlots = useMemo(() => {
    if (!time?.length) return [];

    // Convert duration to milliseconds
    let durationInMs = 0;
    if (duration) {
      // Extract the number part from strings like "30min", "45min", "60min"
      const durationValue = parseInt(duration.toString(), 10);
      if (!isNaN(durationValue)) {
        durationInMs = durationValue * 60 * 1000; // Convert minutes to milliseconds
      }
    }

    // Add buffer time (duration) to current time for minimum booking time
    const minBookingTime = new Date(currentTime.getTime() + durationInMs);

    return time.map(slot => {
      const slotTime = new Date(slot.rawTime);
      return {
        ...slot,
        disabled: slot.disabled || slotTime <= minBookingTime,
      };
    });
  }, [time, duration, currentTime]);

  const options = useMemo(() => {
    if (!processedTimeSlots?.length) {
      return [];
    }

    if (isProvider) {
      // For providers, show all non-disabled time slots
      return processedTimeSlots.reduce((total: OptionType[], current) => {
        if (!current.disabled) {
          return [
            ...total,
            {
              value: current.value,
              label: current.value,
            },
          ];
        }
        return total;
      }, []);
    }

    // For non-providers, check if selected date is today
    const isSelectedDateToday =
      now.toDateString() === watch('date')?.toDateString();

    if (isSelectedDateToday) {
      return [];
    }

    // For future dates, filter slots that are at least 24 hours ahead
    return processedTimeSlots.reduce((total: OptionType[], current) => {
      if (
        Date.parse(`${current.rawTime}`) >= twentyFourHours &&
        !current.disabled
      ) {
        return [
          ...total,
          {
            value: current.value,
            label: current.value,
          },
        ];
      }
      return total;
    }, []);
  }, [
    time,
    watch('date')?.toDateString(),
    duration,
    isProvider,
    processedTimeSlots,
    twentyFourHours,
  ]);

  useEffect(() => {
    if (loadingStatus === SliceStatus.pending) {
      setOptionsLoaded(true);
    }
  }, [loadingStatus]);

  useEffect(() => {
    if (!props.selectedDate || !options.length || watch('time')) {
      return;
    }

    const date = format(props.selectedDate.toDate(), 'h:mm aaaa');
    const selectedHour = date.split(':')[0];
    const selectedAMPM = date.split(' ')[1];

    const value = processedTimeSlots?.find(
      t => t.value.startsWith(selectedHour) && t.value.endsWith(selectedAMPM),
    )?.value;

    props.setValue!('time', value ?? '');
  }, [options]);

  // Auto-select the first available slot as default
  useEffect(() => {
    // Only auto-select when we have slots and the setValue function
    if (!processedTimeSlots?.length || !props.setValue) {
      return;
    }

    // Get current time value
    const currentTimeValue = watch('time');

    // Check if the current time selection is still valid after duration change
    const isCurrentTimeValid =
      currentTimeValue &&
      processedTimeSlots.some(
        slot => slot.value === currentTimeValue && !slot.disabled,
      );

    // Only reset selection if there's no valid selection
    if (!isCurrentTimeValid) {
      // Find the first non-disabled slot
      const firstAvailableSlot = processedTimeSlots.find(
        slot => !slot.disabled,
      );

      if (firstAvailableSlot) {
        props.setValue('time', firstAvailableSlot.value);
      }
    }
  }, [processedTimeSlots, duration]);

  if (!processedTimeSlots?.length) {
    return null;
  }

  return (
    <Tooltip
      content={t('noAvailableSlotsForDay')}
      placement="bottom"
      disabled={!!options.length || !optionsLoaded}
    >
      <div className="w-full overflow-hidden">
        <FormSelect
          control={control!}
          id="time"
          label={null}
          isLoading={loadingStatus === SliceStatus.pending}
          loadingMessage={() => 'Loading'}
          placeholder={
            optionsLoaded && !options.length
              ? t('noAvailableSlots')
              : t('availableSlots')
          }
          onChange={(value, setValue) => setValue(value?.value)}
          detachedSearchBar={true}
          isMulti={false}
          styles={{
            ...detachedSearchBarStyles,
            detachedButton: (isOpen: boolean, hasValue: boolean) => {
              const styles = detachedSearchBarStyles.detachedButton!(
                isOpen,
                hasValue,
              );
              return {
                ...styles,
                borderColor: isOpen ? '#2E62EC' : 'rgb(204 204 204)',
                color: hasValue ? 'black' : '#6B6B6B',
              };
            },
          }}
          extractValue={true}
          options={options}
        />
      </div>
    </Tooltip>
  );
};
